import React, { useState, useEffect } from 'react';
import { Check, Zap, Crown, Star } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SubscriptionPlans = () => {
  const { user, token } = useAuth();
  const { toast } = useToast();
  const [packages, setPackages] = useState({});
  const [loading, setLoading] = useState(false);
  const [processingPlan, setProcessingPlan] = useState(null);

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/payments/packages`);
      setPackages(response.data.packages);
    } catch (error) {
      console.error('Error fetching packages:', error);
    }
  };

  const handleSubscribe = async (packageId) => {
    if (!user) {
      toast({
        variant: 'destructive',
        title: 'Login Required',
        description: 'Please login to subscribe',
      });
      return;
    }

    setLoading(true);
    setProcessingPlan(packageId);

    try {
      const response = await axios.post(
        `${API_URL}/api/payments/checkout/session`,
        {
          package_id: packageId,
          origin_url: window.location.origin,
          currency: 'usd'
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Redirect to Stripe Checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Payment Error',
        description: error.response?.data?.detail || 'Failed to create checkout session',
      });
      setLoading(false);
      setProcessingPlan(null);
    }
  };

  const plans = [
    {
      id: 'basic',
      name: packages.basic?.name || 'Basic Plan',
      price: packages.basic?.price || 9.99,
      icon: <Zap className="w-8 h-8 text-blue-400" />,
      color: 'from-blue-500 to-cyan-500',
      features: [
        'Access basic content library',
        'Watch on 1 device',
        'SD quality',
        'Email support',
        'Ad-supported experience'
      ],
      popular: false
    },
    {
      id: 'pro',
      name: packages.pro?.name || 'Pro Plan',
      price: packages.pro?.price || 19.99,
      icon: <Star className="w-8 h-8 text-yellow-400" />,
      color: 'from-yellow-500 to-orange-500',
      features: [
        'Full content library access',
        'Watch on 2 devices simultaneously',
        'HD quality',
        'Creator tools & analytics',
        'Priority email support',
        'Ad-free experience',
        'Download for offline viewing'
      ],
      popular: true
    },
    {
      id: 'premium',
      name: packages.premium?.name || 'Premium Plan',
      price: packages.premium?.price || 49.99,
      icon: <Crown className="w-8 h-8 text-purple-400" />,
      color: 'from-purple-500 to-pink-500',
      features: [
        'Everything in Pro',
        'Watch on 4 devices simultaneously',
        '4K Ultra HD quality',
        'Advanced creator dashboard',
        'Revenue sharing program',
        '24/7 priority support',
        'Early access to new features',
        'Exclusive creator events',
        'Custom branding options'
      ],
      popular: false
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-purple-900/20 to-black py-16 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-black bg-gradient-to-r from-red-500 via-yellow-500 via-green-500 via-blue-500 to-purple-500 bg-clip-text text-transparent mb-4">
            Choose Your Plan
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Support Black creators and get access to exclusive content
          </p>
          <div className="mt-6 inline-flex items-center gap-2 bg-green-500/10 border border-green-500/20 rounded-full px-4 py-2">
            <Check className="w-5 h-5 text-green-400" />
            <span className="text-green-400 text-sm font-semibold">
              All plans support multi-currency payments
            </span>
          </div>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {plans.map((plan) => (
            <Card
              key={plan.id}
              className={`relative bg-gray-900 border-2 ${
                plan.popular ? 'border-yellow-500 shadow-xl shadow-yellow-500/20' : 'border-gray-700'
              } overflow-hidden transition-transform hover:scale-105`}
            >
              {plan.popular && (
                <div className="absolute top-0 right-0 bg-gradient-to-r from-yellow-400 to-orange-500 text-black text-xs font-bold px-3 py-1 rounded-bl-lg">
                  MOST POPULAR
                </div>
              )}

              <CardHeader>
                <div className={`w-16 h-16 rounded-full bg-gradient-to-r ${plan.color} p-3 mb-4`}>
                  {plan.icon}
                </div>
                <CardTitle className="text-2xl font-bold text-white">
                  {plan.name}
                </CardTitle>
                <div className="mt-4">
                  <span className="text-5xl font-black text-white">
                    ${plan.price}
                  </span>
                  <span className="text-gray-400">/month</span>
                </div>
              </CardHeader>

              <CardContent>
                <Button
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={loading}
                  className={`w-full mb-6 bg-gradient-to-r ${plan.color} hover:opacity-90 text-white font-semibold py-3 text-lg`}
                >
                  {loading && processingPlan === plan.id ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin">⏳</span>
                      Processing...
                    </span>
                  ) : (
                    'Subscribe Now'
                  )}
                </Button>

                <div className="space-y-3">
                  {plan.features.map((feature) => (
                    <div key={`${plan.id}-${feature}`} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-300 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Currency Support */}
        <div className="mt-12 text-center">
          <p className="text-gray-400 mb-4">Accepted payment methods worldwide</p>
          <div className="flex justify-center gap-6 flex-wrap">
            <Badge variant="outline" className="border-gray-600 text-gray-300 px-4 py-2">
              💳 Credit Card
            </Badge>
            <Badge variant="outline" className="border-gray-600 text-gray-300 px-4 py-2">
              🌍 Multi-Currency
            </Badge>
            <Badge variant="outline" className="border-gray-600 text-gray-300 px-4 py-2">
              🔒 Secure Payment
            </Badge>
            <Badge variant="outline" className="border-gray-600 text-gray-300 px-4 py-2">
              🔄 Cancel Anytime
            </Badge>
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-16 max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-8">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            <Card className="bg-gray-900 border-gray-700">
              <CardContent className="pt-6">
                <h3 className="text-white font-semibold mb-2">
                  What currencies do you accept?
                </h3>
                <p className="text-gray-400 text-sm">
                  We accept all major currencies worldwide. Stripe automatically converts prices to your local currency.
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-gray-900 border-gray-700">
              <CardContent className="pt-6">
                <h3 className="text-white font-semibold mb-2">
                  Can I cancel anytime?
                </h3>
                <p className="text-gray-400 text-sm">
                  Yes! You can cancel your subscription at any time. You'll continue to have access until the end of your billing period.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gray-900 border-gray-700">
              <CardContent className="pt-6">
                <h3 className="text-white font-semibold mb-2">
                  How do I upgrade or downgrade my plan?
                </h3>
                <p className="text-gray-400 text-sm">
                  You can change your plan anytime from your account settings. Changes take effect immediately.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPlans;
